import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  BellIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  InformationCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AdminFunctions from '../admin/AdminFunctions';
import DataCleanup from '../admin/DataCleanup';
import { ChangePasswordModal, DeleteAccountModal } from './components';
import { formatDate } from '../../utils/dateUtils';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bankAccount: {
      accountNumber: '',
      bankName: ''
    }
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const fetchProfile = useCallback(async () => {
    try {
      if (user) {
        setProfile(user);
        setFormData({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          bankAccount: {
            accountNumber: user.bankAccount?.accountNumber || '',
            bankName: user.bankAccount?.bankName || ''
          }
        });
      } else {
        const response = await axios.get('/api/users/profile');
        setProfile(response.data);
        setFormData({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          bankAccount: {
            accountNumber: response.data.bankAccount?.accountNumber || '',
            bankName: response.data.bankAccount?.bankName || ''
          }
        });
      }
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user, fetchProfile]);

  const handleProfileChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBankAccountChange = (field, value) => {
    setFormData({
      ...formData,
      bankAccount: {
        ...formData.bankAccount,
        [field]: value
      }
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await updateProfile(formData);
      if (result.success) {
        setEditing(false);
        fetchProfile();
      }
    } catch (error) {
      // Error handled by context
    }
  };

  const handlePasswordSubmit = async (currentPassword, newPassword) => {
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result.success) {
        setChangingPassword(false);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setFormData({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      bankAccount: {
        accountNumber: profile?.bankAccount?.accountNumber || '',
        bankName: profile?.bankAccount?.bankName || ''
      }
    });
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await axios.delete('/api/users/account');
      if (response.data.success) {
        toast.success('Your account has been permanently deleted');
        window.location.href = '/login';
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete account');
    }
  };

  if (loading && !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6 profile-card-mobile">
          <div className="settings-header-mobile">
            <div>
              <h1 className="text-2xl font-bold text-primary">
                {profile?.role === 'admin' ? 'Settings' : 'Profile'}
              </h1>
              <p className="text-secondary mt-1">
                {profile?.role === 'admin' 
                  ? 'Manage application settings and your account information'
                  : 'Manage your account information and settings'
                }
              </p>
            </div>
            <div className="flex items-center space-x-3 settings-buttons-mobile">
              <button
                onClick={() => setEditing(!editing)}
                className="inline-flex items-center px-4 py-2 border border-primary rounded-lg shadow-sm text-sm font-medium text-secondary bg-card hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {editing ? 'Cancel' : 'Edit Profile'}
              </button>
              <button
                onClick={() => setChangingPassword(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <ShieldCheckIcon className="h-4 w-4 mr-2" />
                Change Password
              </button>
            </div>
          </div>
        </div>

        {/* Admin Tabs */}
        {profile?.role === 'admin' && (
          <div className="admin-tabs-container">
            <div className="admin-tabs-nav">
              <button
                onClick={() => setActiveTab('profile')}
                className={`admin-tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              >
                <UserIcon className="admin-tab-icon" />
                Profile & Settings
              </button>
              <button
                onClick={() => setActiveTab('functions')}
                className={`admin-tab-button ${activeTab === 'functions' ? 'active' : ''}`}
              >
                <InformationCircleIcon className="admin-tab-icon" />
                System Functions
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`admin-tab-button ${activeTab === 'stats' ? 'active' : ''}`}
              >
                <ChartBarIcon className="admin-tab-icon" />
                Database Stats
              </button>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'profile' && (
          <div>
            {/* Profile Information */}
            <div className="bg-card rounded-xl shadow-sm border border-primary p-6 profile-card-mobile">
              <h2 className="text-lg font-semibold text-primary mb-6">Personal Information</h2>
              
              {editing ? (
                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleProfileChange}
                        className="form-input block w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleProfileChange}
                        className="form-input block w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleProfileChange}
                      className="form-input block w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleProfileChange}
                      className="form-input block w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  
                  {/* Bank Account Details - Admin Only */}
                  {profile?.role === 'admin' && (
                    <div className="border-t border-primary pt-6 mt-6">
                      <h3 className="text-lg font-medium text-primary mb-4 mt-4">Bank Account Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary mb-2">Account Name</label>
                          <input
                            type="text"
                            name="bankName"
                            value={formData.bankAccount.bankName}
                            onChange={(e) => handleBankAccountChange('bankName', e.target.value)}
                            className="form-input block w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary mb-2">Account Number</label>
                          <input
                            type="text"
                            name="accountNumber"
                            value={formData.bankAccount.accountNumber}
                            onChange={(e) => handleBankAccountChange('accountNumber', e.target.value)}
                            className="form-input block w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-4 py-2 border border-primary rounded-lg text-sm font-medium text-secondary bg-card hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <XMarkIcon className="h-4 w-4 mr-2 inline" />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <CheckIcon className="h-4 w-4 mr-2 inline" />
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-3">
                      <UserIcon className="h-5 w-5 text-muted" />
                      <div>
                        <p className="text-sm font-medium text-tertiary">First Name</p>
                        <p className="text-sm text-primary">{profile?.firstName || 'Not provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <UserIcon className="h-5 w-5 text-muted" />
                      <div>
                        <p className="text-sm font-medium text-tertiary">Last Name</p>
                        <p className="text-sm text-primary">{profile?.lastName || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-3">
                      <EnvelopeIcon className="h-5 w-5 text-muted" />
                      <div>
                        <p className="text-sm font-medium text-tertiary">Email Address</p>
                        <p className="text-sm text-primary">{profile?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="h-5 w-5 text-muted" />
                      <div>
                        <p className="text-sm font-medium text-tertiary">Phone Number</p>
                        <p className="text-sm text-primary">{profile?.phone || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <CalendarIcon className="h-5 w-5 text-muted" />
                    <div>
                      <p className="text-sm font-medium text-tertiary">
                        {profile?.role === 'admin' ? 'Account Created' : 'Member Since'}
                      </p>
                      <p className="text-sm text-primary">
                        {profile?.createdAt ? formatDate(profile.createdAt, 'MMM d, yyyy') : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Bank Account Details Display - Admin Only */}
                  {profile?.role === 'admin' && profile?.bankAccount && (
                    <div className="border-t border-primary pt-6 mt-6">
                      <h3 className="text-lg font-medium text-primary mb-4 mt-4">Bank Account Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-center space-x-3">
                          <ShieldCheckIcon className="h-5 w-5 text-muted" />
                          <div>
                            <p className="text-sm font-medium text-tertiary">Account Name</p>
                            <p className="text-sm text-primary">{profile.bankAccount.bankName || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <ShieldCheckIcon className="h-5 w-5 text-muted" />
                          <div>
                            <p className="text-sm font-medium text-tertiary">Account Number</p>
                            <p className="text-sm text-primary">{profile.bankAccount.accountNumber || 'Not provided'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Notifications card - Only for regular users */}
              {profile?.role === 'user' && (
                <div className="bg-card rounded-xl shadow-sm border border-primary p-6 mt-8 profile-card-mobile">
                  <Link to="/notification-settings" className="notification-settings-card">
                    <BellIcon className="icon" />
                    <div>
                      <h3 className="title">Notification Settings</h3>
                      <p className="description">Manage your notification preferences</p>
                    </div>
                  </Link>
                </div>
              )}

              {/* Danger Zone - Only for non-admin users */}
              {profile?.role !== 'admin' && (
                <div className="danger-zone-container">
                  <h2 className="danger-zone-title">Danger Zone</h2>
                  
                  <div className="danger-zone-warning-box">
                    <div className="flex items-start space-x-3">
                      <ExclamationTriangleIcon className="danger-zone-icon h-5 w-5 mt-0.5" />
                      <div>
                        <h3 className="danger-zone-warning-title">Delete Account</h3>
                        <p className="danger-zone-warning-text">
                          Once you delete your account, there is no going back. This will permanently delete your account, 
                          all your bookings, and remove you from any waitlists. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="delete-account-button"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete Account
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Functions Tab Content */}
            {profile?.role === 'admin' && activeTab === 'functions' && (
              <AdminFunctions />
            )}
          </div>
        )}

        {/* Functions Tab - Show when active */}
        {activeTab === 'functions' && profile?.role === 'admin' && (
          <AdminFunctions />
        )}

        {/* Database Stats Tab - Show when active */}
        {activeTab === 'stats' && profile?.role === 'admin' && (
          <DataCleanup />
        )}
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={changingPassword}
        onClose={() => setChangingPassword(false)}
        onSubmit={handlePasswordSubmit}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
      />
    </>
  );
};

export default Profile;
